import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Loader,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pill } from '../../components/ui/Pill';
import type { PillTone } from '../../components/ui/Pill';
import { RunningState } from '../../components/ui/RunningState';
import { StatCard } from '../../components/ui/StatCard';
import { JobType } from '../../graphql/types';
import type { Document, GuidelineRecord } from '../../graphql/types';
import { getLoanTitle } from '../LoanRail';
import { Rail } from '../Rail';
import { TOP_BAR_HEIGHT_PX } from '../TopBar';
import { useWorkflowWorkspace } from '../WorkflowWorkspace';
import { groupExtractFields, flattenGroups } from '../extract/extractModel';
import type { ExtractField } from '../extract/extractModel';
import * as analyzeData from './analyzeData';
import type { AnalyzeContext, StoredAnalysis } from './analyzeData';
import {
  FINDING_KIND_LABELS,
  FINDING_STATUS_LABELS,
  buildAnalyze,
  findingsForLoan,
  linkFindingsToFields,
  scopeLoanIds,
  summarize,
} from './analyzeModel';
import type { Finding, FindingStatus, LoanInfo } from './analyzeModel';
import { buildOpenFieldParams } from './analyzeNavigation';
import { loadFieldChanges, loadRunRecord, markStale, saveRunRecord } from './analyzeStale';
import type { FieldChanges, RunRecord } from './analyzeStale';

/**
 * AnalyzeMode (DD and BA), built from board `3a` (job-wide) and `3b` (one loan).
 *
 * Top to bottom: the guideline bar, two runs, three stat cards, the findings
 * table.
 *
 * - Run 1 checks completeness and consistency as one pass.
 * - Run 2 is the guideline review. It has its own scope (eligible loans or all
 *   loans) and says how many loans are held back until their required
 *   documents are complete (Requirements 9.2, 9.3).
 * - A finding that comes from a field offers "Open field in Extract", which
 *   opens Extract with that field selected (9.4).
 * - A finding is marked "Out of date" once its field was confirmed or changed
 *   after the run (9.5).
 * - Picking a loan (Rail, or a loan name in the table) shows the single-loan
 *   view. Both views are computed from the same findings, so counts and badges
 *   agree (9.6).
 *
 * The job and the `loan` selection come from `useWorkflowWorkspace()`.
 * The Rail belongs to the Workspace (`WorkflowRail`: loans with an "All loans"
 * row for DD, the loan's documents for BA), so this body has none. The `rail`
 * prop is only for tests and previews that render the Mode on its own.
 * - "Re-run on changed loans" refreshes the out-of-date findings: checks when a
 *   completeness or consistency finding is out of date, review when a guideline
 *   finding is. The routes cover the whole job (design.md, Backend gaps), so the
 *   run is job-wide and the button is named for the loans that triggered it.
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6.
 */
export interface AnalyzeModeProps {
  /** For tests and previews only: a Rail rendered inside the body. The Workspace owns the real one. */
  rail?: React.ReactNode;
  /** Accessible name of the Rail. */
  railLabel?: string;
}

const PRIMARY_BUTTON =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-[#834DFB] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#6E2EF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#834DFB]';

const SECONDARY_BUTTON =
  'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#DDD0FF] bg-white px-3 py-1.5 text-[13px] font-bold text-[#5A21C9] transition-colors hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50';

/** Severity shown at the start of a row, as in the prototype: Fail is red, Warning is amber. */
const SEVERITY: Record<FindingStatus, { label: string; tone: PillTone }> = {
  FAIL: { label: 'Fail', tone: 'danger' },
  ERROR: { label: 'Warning', tone: 'warning' },
  MISSING_DATA: { label: 'Warning', tone: 'warning' },
  WARNING: { label: 'Warning', tone: 'warning' },
  PASS: { label: 'Pass', tone: 'success' },
};

type Scope = 'eligible' | 'all';

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

function formatRunTime(at: number | undefined): string {
  if (!at) return 'Results from an earlier run';
  return `Last run ${new Date(at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`;
}

export const AnalyzeMode: React.FC<AnalyzeModeProps> = ({ rail, railLabel = 'Loans' }) => {
  const { job, jobType, selection, select, reportStatus, reportAnalysis } = useWorkflowWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = job.jobId;
  const isBA = jobType === JobType.BA;

  // ── Job context: loans, documents, guidelines ─────────────────────────────
  const [ctx, setCtx] = useState<AnalyzeContext | null>(null);
  const [guidelines, setGuidelines] = useState<GuidelineRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const context = await analyzeData.loadAnalyzeContext(jobId);
        const list = await analyzeData.loadGuidelines(context.tenantId, jobType);
        if (cancelled) return;
        setCtx(context);
        setGuidelines(list);
      } catch (err) {
        console.error('[AnalyzeMode] Failed to load the job:', err);
        if (!cancelled) {
          setLoadError('The loans and guidelines could not be loaded. Check your connection and try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, jobType, loadKey]);

  // ── Guideline ─────────────────────────────────────────────────────────────
  const [guidelineId, setGuidelineId] = useState('');
  const [changingGuideline, setChangingGuideline] = useState(false);
  const guideline = guidelines.find((g) => g.guidelineId === guidelineId);

  useEffect(() => {
    if (guidelines.length === 0) return;
    let remembered: string | null = null;
    try {
      remembered = window.localStorage.getItem(`lastGuideline_${jobId}`);
    } catch {
      remembered = null;
    }
    if (remembered && guidelines.some((g) => g.guidelineId === remembered)) {
      setGuidelineId(remembered);
    } else if (guidelines.length === 1) {
      setGuidelineId(guidelines[0].guidelineId);
    }
  }, [guidelines, jobId]);

  const chooseGuideline = (id: string) => {
    setGuidelineId(id);
    setChangingGuideline(false);
    try {
      if (id) window.localStorage.setItem(`lastGuideline_${jobId}`, id);
    } catch {
      // Not remembering the choice is harmless.
    }
  };

  // ── Stored results, run record and field changes ──────────────────────────
  const [stored, setStored] = useState<StoredAnalysis>({
    completeness: null,
    consistency: null,
    guideline: null,
  });
  const [storedLoading, setStoredLoading] = useState(false);
  const [record, setRecord] = useState<RunRecord>({});
  const [changes, setChanges] = useState<FieldChanges>({});

  useEffect(() => {
    setChanges(loadFieldChanges(jobId));
    if (!ctx || !guidelineId) {
      setStored({ completeness: null, consistency: null, guideline: null });
      setRecord({});
      return;
    }
    let cancelled = false;
    setStoredLoading(true);
    setRecord(loadRunRecord(jobId, guidelineId));
    analyzeData
      .loadStoredAnalysis(jobId, ctx.tenantId, guidelineId)
      .then((result) => {
        if (!cancelled) setStored(result);
      })
      .finally(() => {
        if (!cancelled) setStoredLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ctx, guidelineId, jobId]);

  // ── Derived findings ──────────────────────────────────────────────────────
  const loans = useMemo<LoanInfo[]>(
    () => (ctx?.loans ?? []).map((l) => ({ loanId: l.loanId, label: getLoanTitle(l) })),
    [ctx]
  );

  const { docTypes, fieldsByLoan } = useMemo(() => {
    const types: Record<string, string[]> = {};
    const byLoanDocs: Record<string, Document[]> = {};
    for (const doc of ctx?.documents ?? []) {
      (types[doc.loanId] ??= []).push(doc.documentType);
      (byLoanDocs[doc.loanId] ??= []).push(doc);
    }
    const fields: Record<string, ExtractField[]> = {};
    for (const [loanId, docs] of Object.entries(byLoanDocs)) {
      fields[loanId] = flattenGroups(groupExtractFields(docs));
    }
    return { docTypes: types, fieldsByLoan: fields };
  }, [ctx]);

  const result = useMemo(() => {
    const base = buildAnalyze({
      loans,
      docTypes,
      completeness: stored.completeness,
      consistency: stored.consistency,
      guideline: stored.guideline,
      guidelineScope: record.guidelineScope ?? null,
    });
    const linked = linkFindingsToFields(base.findings, fieldsByLoan);
    return { ...base, findings: markStale(linked, changes, record) };
  }, [loans, docTypes, fieldsByLoan, stored, record, changes]);

  // ── Which view: job-wide (3a) or one loan (3b) ────────────────────────────
  const requestedLoan = selection.loan;
  const activeLoan =
    loans.find((l) => l.loanId === requestedLoan) ?? (isBA && loans.length === 1 ? loans[0] : undefined);
  const activeLoanId = activeLoan?.loanId ?? null;
  const summary = useMemo(() => summarize(result, activeLoanId), [result, activeLoanId]);

  // The Workspace Rail shows each loan's status line from these findings.
  const contextLoaded = !!ctx;
  useEffect(() => {
    if (contextLoaded) reportAnalysis(result);
  }, [contextLoaded, result, reportAnalysis]);

  // Open findings for the Status_Pill ("Guideline review · 2 open", BA). Only
  // reported once a run exists, so an unrun job keeps the stage wording.
  const anyRun = result.ran.completeness || result.ran.consistency || result.ran.guideline;
  const openFindings = result.findings.filter((f) => f.status !== 'PASS').length;
  useEffect(() => {
    if (anyRun) reportStatus({ openFindings });
  }, [anyRun, openFindings, reportStatus]);

  const [showPassedChoice, setShowPassedChoice] = useState<boolean | null>(null);
  const showPassed = showPassedChoice ?? activeLoanId !== null;
  const rows = useMemo(
    () => findingsForLoan(result.findings, activeLoanId).filter((f) => showPassed || f.status !== 'PASS'),
    [result.findings, activeLoanId, showPassed]
  );

  const heldBackInView = useMemo(
    () => result.heldBack.filter((h) => activeLoanId === null || h.loanId === activeLoanId),
    [result.heldBack, activeLoanId]
  );
  const [showHeldBack, setShowHeldBack] = useState(false);

  // ── Runs ──────────────────────────────────────────────────────────────────
  const [scope, setScope] = useState<Scope>('eligible');
  const scopeIds = useMemo(() => scopeLoanIds(result, scope), [result, scope]);

  const [checksRunning, setChecksRunning] = useState(false);
  const [checksError, setChecksError] = useState<string | null>(null);
  const [reviewRunning, setReviewRunning] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const checksBlock = !guidelineId
    ? 'Choose a guideline to run the checks.'
    : loans.length === 0
      ? 'This job has no loans yet.'
      : undefined;

  const reviewBlock = !guidelineId
    ? 'Choose a guideline to run the review.'
    : !result.ran.completeness
      ? 'Run completeness and consistency first, so the loans to hold back are known.'
      : (activeLoanId ? !scopeIds.includes(activeLoanId) : scopeIds.length === 0)
        ? 'These loans are held back until their required documents are complete. Resolve them, or choose All loans.'
        : undefined;

  /** Resolves true when both checks ran. */
  const handleRunChecks = async (): Promise<boolean> => {
    if (checksBlock || checksRunning || !guidelineId) return false;
    let ok = false;
    setChecksRunning(true);
    setChecksError(null);
    try {
      const run = await analyzeData.runChecks(jobId, guidelineId);
      setStored((prev) => ({
        ...prev,
        completeness: run.completeness ?? prev.completeness,
        consistency: run.consistency ?? prev.consistency,
      }));
      if (run.failed.length === 0) {
        setRecord(saveRunRecord(jobId, guidelineId, { checksAt: Date.now() }));
        ok = true;
      } else {
        setChecksError(
          `The ${run.failed.join(' and ')} check could not run. The other check was updated. Try again.`
        );
      }
    } catch (err) {
      setChecksError(err instanceof Error ? err.message : 'The checks could not run. Try again.');
    } finally {
      setChecksRunning(false);
    }
    return ok;
  };

  /** `keepScope` repeats the scope of the last review instead of the scope selector. */
  const handleRunReview = async (keepScope = false) => {
    if ((!keepScope && reviewBlock) || reviewRunning || !guidelineId) return;
    setReviewRunning(true);
    setReviewError(null);
    try {
      const findings = await analyzeData.runReview(jobId, guidelineId);
      setStored((prev) => ({ ...prev, guideline: findings }));
      setRecord(
        saveRunRecord(jobId, guidelineId, {
          guidelineAt: Date.now(),
          guidelineScope: keepScope ? (record.guidelineScope ?? null) : scope === 'all' ? null : scopeIds,
        })
      );
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'The review could not run. Try again.');
    } finally {
      setReviewRunning(false);
    }
  };

  // ── Re-run on changed loans (Requirement 9.5) ─────────────────────────────
  const staleInView = useMemo(
    () => findingsForLoan(result.findings, activeLoanId).filter((f) => f.stale),
    [result.findings, activeLoanId]
  );
  const staleLoanCount = useMemo(
    () => new Set(staleInView.map((f) => f.loanId).filter(Boolean)).size,
    [staleInView]
  );
  const rerunBlock = !guidelineId
    ? 'Choose a guideline first.'
    : staleInView.length === 0
      ? 'Nothing is out of date. Findings are marked here after you confirm or change a field in Extract.'
      : undefined;

  const handleRerunChanged = async () => {
    if (rerunBlock || checksRunning || reviewRunning) return;
    const needsChecks = staleInView.some((f) => f.kind !== 'guideline');
    const needsReview = staleInView.some((f) => f.kind === 'guideline');
    if (needsChecks && !(await handleRunChecks())) return;
    if (needsReview) await handleRunReview(true);
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const showAllLoans = () => select({ loan: null, doc: null, field: null, exception: null });
  const openLoan = (loanId: string) =>
    select({ loan: loanId, doc: null, field: null, exception: null });
  const openField = useCallback(
    (finding: Finding) => {
      if (finding.fieldRef) setSearchParams(buildOpenFieldParams(searchParams, finding.fieldRef));
    },
    [searchParams, setSearchParams]
  );

  const isSelectedFinding = (f: Finding) => {
    const wanted = selection.field;
    if (!f.fieldRef || !wanted) return false;
    if (selection.loan && selection.loan !== f.fieldRef.loanId) return false;
    return f.fieldRef.fieldId === wanted || f.fieldRef.fieldId.endsWith(`:${wanted}`);
  };

  // ── Rail ──────────────────────────────────────────────────────────────────
  const guidelineCaption = isBA
    ? 'Judging this loan against'
    : activeLoan
      ? `Judging ${activeLoan.label} against`
      : `Judging all ${loans.length} loans against`;

  // ── Cards ─────────────────────────────────────────────────────────────────
  const completenessCard = !summary.ran.completeness ? (
    <StatCard label="Required documents" value="Not run" hint="Run the checks to find missing documents." />
  ) : (
    <StatCard
      label="Required documents"
      value={
        <span className={summary.completeLoans < summary.loanCount ? 'text-[#b91c1c]' : undefined}>
          {summary.completeLoans} of {summary.loanCount}
        </span>
      }
      hint={summary.loanCount === 1 ? 'loan complete' : 'loans complete'}
    />
  );

  const consistencyCard = !summary.ran.consistency ? (
    <StatCard label="Consistency" value="Not run" hint="Run the checks to compare the documents." />
  ) : (
    <StatCard
      label="Consistency"
      value={
        summary.openConsistency === 0 ? (
          'Clean'
        ) : (
          <span className="text-[#a16207]">{summary.openConsistency} open</span>
        )
      }
      hint={summary.openConsistency === 0 ? 'no conflicts across documents' : 'conflicts across documents'}
    />
  );

  const guidelineCard =
    summary.ran.guideline && summary.reviewedLoans > 0 ? (
      <StatCard
        label="Guideline review"
        value={`${summary.passedLoans} of ${summary.reviewedLoans}`}
        hint={`pass · ${summary.heldBackLoans} held back`}
      />
    ) : summary.ran.completeness ? (
      <StatCard
        label="Guideline review"
        value={`${summary.eligibleLoans} eligible`}
        hint={`${plural(summary.heldBackLoans, 'loan')} held back`}
      />
    ) : (
      <StatCard label="Guideline review" value="Not run" hint="Run the checks first." />
    );

  // ── Render ────────────────────────────────────────────────────────────────
  let body: React.ReactNode;
  if (loading && !ctx) {
    body = <RunningState title="Loading analysis" description="Getting the loans and guidelines for this job." />;
  } else if (loadError) {
    body = (
      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} strokeWidth={2} className="mt-0.5 shrink-0 text-red-600" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-red-800">{loadError}</p>
            <button type="button" onClick={() => setLoadKey((k) => k + 1)} className={`${SECONDARY_BUTTON} mt-3`}>
              <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  } else if (loans.length === 0) {
    body = (
      <EmptyState
        icon={<ClipboardCheck size={22} strokeWidth={2} aria-hidden="true" />}
        title="No loans to analyze"
        description="Add loans and documents in Intake, extract them, then come back to run the checks."
      />
    );
  } else {
    body = (
      <>
        {/* Guideline bar (Requirement 9.1) */}
        <section
          aria-label="Guideline"
          data-testid="guideline-bar"
          className="flex items-center gap-5 rounded-xl bg-[#1c1428] px-5 py-3"
        >
          <div className="min-w-0 flex-1">
            <p data-testid="guideline-caption" className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#b8afd0]">
              {guidelineCaption}
            </p>
            {changingGuideline || (!guideline && guidelines.length > 0) ? (
              <select
                aria-label="Guideline"
                value={guidelineId}
                onChange={(e) => chooseGuideline(e.target.value)}
                className="mt-1 w-full max-w-md rounded-lg border border-white/25 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F0E100]"
              >
                <option value="">Choose a guideline</option>
                {guidelines.map((g) => (
                  <option key={g.guidelineId} value={g.guidelineId}>
                    {g.name}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <p data-testid="guideline-name" className="mt-0.5 truncate text-[19px] font-bold text-white">
                  {guideline?.name ?? 'No guideline available'}
                </p>
                {guideline?.description && (
                  <p className="mt-0.5 truncate text-xs text-[#c8c0dd]">{guideline.description}</p>
                )}
                {!guideline && (
                  <p className="mt-0.5 text-xs text-[#c8c0dd]">
                    No guidelines are set up for your organisation. Ask your administrator to add one.
                  </p>
                )}
              </>
            )}
          </div>
          {guidelines.length > 0 && (
            <button
              type="button"
              disabled={checksRunning || reviewRunning}
              onClick={() => setChangingGuideline((v) => !v)}
              className="whitespace-nowrap rounded-lg border border-white/25 bg-white/10 px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F0E100] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {changingGuideline ? 'Done' : 'Change guideline'}
            </button>
          )}
        </section>

        {/* The two runs (Requirement 9.2) */}
        <div className="grid grid-cols-2 gap-4">
          <section
            aria-label="Completeness and consistency"
            data-testid="run-checks"
            className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4"
          >
            <div>
              <h3 className="text-base font-bold text-gray-900">Completeness and consistency</h3>
              <p className="mt-0.5 text-sm text-gray-600">
                Checks that every required document is present and that the documents agree with each
                other. One run covers both.
              </p>
            </div>
            <p className="text-xs text-gray-500" data-testid="checks-status">
              {storedLoading
                ? 'Loading earlier results…'
                : result.ran.completeness || result.ran.consistency
                  ? formatRunTime(record.checksAt)
                  : 'Not run yet'}
            </p>
            {checksError && (
              <p role="alert" className="text-xs text-[#991b1b]">
                {checksError}
              </p>
            )}
            <div className="mt-auto">
              <button
                type="button"
                data-testid="run-checks-button"
                onClick={() => void handleRunChecks()}
                disabled={!!checksBlock || checksRunning || reviewRunning}
                title={checksBlock}
                className={PRIMARY_BUTTON}
              >
                {checksRunning ? (
                  <>
                    <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                    Running…
                  </>
                ) : (
                  <>
                    <ClipboardCheck size={14} strokeWidth={2} aria-hidden="true" />
                    {result.ran.completeness || result.ran.consistency ? 'Re-run checks' : 'Run checks'}
                  </>
                )}
              </button>
            </div>
          </section>

          <section
            aria-label="Guideline review"
            data-testid="run-review"
            className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4"
          >
            <div>
              <h3 className="text-base font-bold text-gray-900">Guideline review</h3>
              <p className="mt-0.5 text-sm text-gray-600">
                Reviews loans against the guideline. It only covers loans whose required documents are
                complete.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
              Scope
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as Scope)}
                disabled={reviewRunning}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm font-normal text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
              >
                <option value="eligible">
                  Eligible loans ({scopeLoanIds(result, 'eligible').length})
                </option>
                <option value="all">All loans ({loans.length})</option>
              </select>
            </label>

            <div data-testid="held-back">
              {!result.ran.completeness ? (
                <p className="text-sm text-gray-600">
                  Run the checks first to see which loans are held back.
                </p>
              ) : heldBackInView.length === 0 ? (
                <p className="text-sm text-gray-600">No loans are held back.</p>
              ) : (
                <>
                  <button
                    type="button"
                    aria-expanded={showHeldBack}
                    onClick={() => setShowHeldBack((v) => !v)}
                    className="inline-flex items-center gap-1 rounded text-sm font-bold text-[#5A21C9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                  >
                    {showHeldBack ? (
                      <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
                    ) : (
                      <ChevronRight size={14} strokeWidth={2} aria-hidden="true" />
                    )}
                    {plural(heldBackInView.length, 'loan')} held back
                  </button>
                  <span className="ml-1 text-sm text-gray-600">until their required documents are complete</span>
                  {showHeldBack && (
                    <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
                      {heldBackInView.map((h) => (
                        <li key={h.loanId} className="flex items-center gap-3 px-3 py-2 text-sm">
                          <span className="font-bold text-gray-900">{h.label}</span>
                          <span className="min-w-0 flex-1 truncate text-gray-600" title={h.reasons.join(', ')}>
                            {h.reasons.join(', ')}
                          </span>
                          {activeLoanId === null && (
                            <button type="button" onClick={() => openLoan(h.loanId)} className={SECONDARY_BUTTON}>
                              Open loan
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            <p className="text-xs text-gray-500" data-testid="review-status">
              {summary.ran.guideline
                ? `${formatRunTime(record.guidelineAt)} · ${
                    record.guidelineScope ? plural(record.guidelineScope.length, 'loan') : 'all loans'
                  } in scope`
                : 'Not run yet'}
            </p>
            {reviewError && (
              <p role="alert" className="text-xs text-[#991b1b]">
                {reviewError}
              </p>
            )}
            <div className="mt-auto">
              <button
                type="button"
                data-testid="run-review-button"
                onClick={() => void handleRunReview()}
                disabled={!!reviewBlock || reviewRunning || checksRunning}
                title={reviewBlock}
                className={PRIMARY_BUTTON}
              >
                {reviewRunning ? (
                  <>
                    <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                    Reviewing…
                  </>
                ) : (
                  <>
                    <ShieldCheck size={14} strokeWidth={2} aria-hidden="true" />
                    {summary.ran.guideline ? 'Re-run review' : 'Run review'}
                  </>
                )}
              </button>
            </div>
          </section>
        </div>

        {/* Stat cards (Requirement 9.6: same numbers in both views) */}
        <div data-testid="stat-cards" className="grid grid-cols-3 gap-3.5">
          {completenessCard}
          {consistencyCard}
          {guidelineCard}
        </div>

        {/* Findings */}
        <section aria-label="Findings" className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-900">Findings</h3>
              <span data-testid="finding-count" className="text-sm text-gray-500">
                {plural(rows.length, 'row')}
              </span>
              {summary.stale > 0 && (
                <Pill
                  tone="warning"
                  title="The field behind these findings changed after the last run. Re-run to refresh them."
                >
                  <span data-testid="stale-count">{summary.stale} out of date</span>
                </Pill>
              )}
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={showPassed}
                  onChange={(e) => setShowPassedChoice(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[#834DFB] focus:ring-[#834DFB]"
                />
                Show passed
              </label>
              <button
                type="button"
                data-testid="rerun-changed-button"
                onClick={() => void handleRerunChanged()}
                disabled={!!rerunBlock || checksRunning || reviewRunning}
                title={
                  rerunBlock ??
                  `Re-runs the checks and review for the ${plural(staleLoanCount || 1, 'loan')} with out-of-date findings.`
                }
                className={SECONDARY_BUTTON}
              >
                {checksRunning || reviewRunning ? (
                  <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
                )}
                Re-run on changed loans
              </button>
            </div>
          </header>

          {rows.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title={
                  !guidelineId
                    ? 'Choose a guideline'
                    : result.ran.completeness || result.ran.consistency || result.ran.guideline
                      ? 'No open findings'
                      : 'No results yet'
                }
                description={
                  !guidelineId
                    ? 'Pick the guideline to apply, then run the checks.'
                    : result.ran.completeness || result.ran.consistency || result.ran.guideline
                      ? 'Every check that ran passed. Turn on Show passed to see them.'
                      : 'Run completeness and consistency to find missing documents and conflicts.'
                }
              />
            </div>
          ) : (
            <ul data-testid="findings">
              {rows.map((f) => {
                const loanLabel = f.loanId ? (loans.find((l) => l.loanId === f.loanId)?.label ?? f.loanId) : null;
                const selected = isSelectedFinding(f);
                return (
                  <li
                    key={f.id}
                    data-testid="finding-row"
                    data-status={f.status}
                    data-kind={f.kind}
                    data-stale={f.stale ? 'true' : 'false'}
                    data-loan={f.loanId ?? ''}
                    aria-current={selected ? 'true' : undefined}
                    className={`flex items-start gap-3.5 border-b border-gray-50 px-4 py-3 last:border-b-0 ${
                      selected ? 'bg-[#F5F3FF]' : ''
                    }`}
                  >
                    <Pill tone={SEVERITY[f.status].tone} className="mt-0.5 shrink-0">
                      <span data-testid="finding-severity">{SEVERITY[f.status].label}</span>
                    </Pill>
                    <div data-testid="finding-loan" className="w-28 shrink-0 pt-0.5 text-[13px] font-bold text-gray-900">
                      {loanLabel === null ? (
                        <span className="text-gray-500">All loans</span>
                      ) : activeLoanId === null ? (
                        <button
                          type="button"
                          onClick={() => openLoan(f.loanId as string)}
                          className="max-w-full truncate rounded text-left text-[#5A21C9] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                          title={`Open ${loanLabel}`}
                        >
                          {loanLabel}
                        </button>
                      ) : (
                        loanLabel
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-700">{f.detail}</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {FINDING_STATUS_LABELS[f.status]} · {FINDING_KIND_LABELS[f.kind]}
                        {f.kind !== 'guideline' && f.title !== FINDING_KIND_LABELS[f.kind] ? ` · ${f.title}` : ''}
                      </p>
                      {f.criteria && f.criteria.length > 0 && (
                        <ul className="mt-1 list-disc pl-4 text-xs text-gray-600">
                          {f.criteria.map((c) => (
                            <li key={c}>{c}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {f.stale && (
                      <Pill
                        tone="warning"
                        className="mt-0.5 shrink-0"
                        title="The field behind this finding changed after the last run. Re-run to refresh it."
                      >
                        Out of date
                      </Pill>
                    )}
                    {f.fieldRef && (
                      <button
                        type="button"
                        data-testid="open-field"
                        onClick={() => openField(f)}
                        className={`${SECONDARY_BUTTON} shrink-0 whitespace-nowrap`}
                      >
                        Open field in Extract
                        <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </>
    );
  }

  // `-m-6` cancels the Workspace body padding; the height is the space under the
  // Top_Bar. The Rail is the Workspace's, so this row holds only the body.
  return (
    <div
      data-testid="analyze-mode"
      className="-m-6 flex min-w-0 bg-[#f7f6fb]"
      style={{ height: `calc(100vh - ${TOP_BAR_HEIGHT_PX}px)` }}
    >
      {rail ? (
        <Rail label={railLabel} className="h-full">
          {rail}
        </Rail>
      ) : null}
      <div className="h-full min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-4 p-6">
          {ctx && loans.length > 0 && (
            <div className="flex items-center gap-3">
              {activeLoan && !isBA && (
                <button type="button" onClick={showAllLoans} className={SECONDARY_BUTTON}>
                  <ChevronLeft size={14} strokeWidth={2} aria-hidden="true" />
                  All loans
                </button>
              )}
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
                  {activeLoan ? 'Loan' : 'Whole job'}
                </p>
                <h2 data-testid="analyze-title" className="truncate text-base font-bold text-gray-900">
                  {activeLoan ? activeLoan.label : `${job.name} · ${plural(loans.length, 'loan')}`}
                </h2>
              </div>
            </div>
          )}
          {body}
        </div>
      </div>
    </div>
  );
};

export default AnalyzeMode;
